import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Inbox, Info, Mail, MapPin, Calendar } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

type SignupStatus = "pending" | "approved" | "declined";

interface SignupRow {
  id: string;
  full_name: string;
  email: string;
  location: string | null;
  status: SignupStatus;
  created_at: string;
}

const STATUS_VARIANTS: Record<
  SignupStatus,
  { label: string; variant: "default" | "secondary" | "outline" | "destructive" }
> = {
  pending: { label: "Pending", variant: "secondary" },
  approved: { label: "Approved", variant: "default" },
  declined: { label: "Declined", variant: "outline" },
};

export default function AdminMatchmakerSignups() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SignupStatus>("pending");
  const [rows, setRows] = useState<SignupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data, error: queryError } = await supabase
          .from("matchmaker_signups")
          .select("id, full_name, email, location, status, created_at")
          .eq("status", status)
          .order("created_at", { ascending: false });

        if (queryError) throw queryError;
        if (!cancelled) setRows((data ?? []) as SignupRow[]);
      } catch (err: any) {
        console.error("matchmaker_signups.load_failed");
        if (!cancelled) {
          setError(err.message || "Failed to load signups.");
          toast({
            title: "Error",
            description: err.message || "Failed to load signups.",
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [status]);

  return (
    <AdminLayout title="Matchmaker Signups">
      <div className="container mx-auto p-6 max-w-5xl">
        <div className="mb-8 rounded-2xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-6 border border-primary/20 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <Inbox className="h-7 w-7 text-primary" />
            <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Matchmaker Signups
            </h2>
            {!loading && (
              <Badge variant="secondary" className="ml-2">
                {rows.length}
              </Badge>
            )}
          </div>
          <p className="text-foreground/70">
            Review applications submitted via the public matchmaker signup form.
          </p>
        </div>

        <Tabs
          value={status}
          onValueChange={(v) => setStatus(v as SignupStatus)}
          className="mb-6"
        >
          <TabsList>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="approved">Approved</TabsTrigger>
            <TabsTrigger value="declined">Declined</TabsTrigger>
          </TabsList>
        </Tabs>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <Info className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <Alert>
            <Inbox className="h-4 w-4" />
            <AlertTitle>Nothing here</AlertTitle>
            <AlertDescription>
              No {status} signups right now.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => {
              const sv = STATUS_VARIANTS[row.status];
              return (
                <Card
                  key={row.id}
                  className="hover:border-primary/50 transition-colors"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-lg">
                            {row.full_name}
                          </h3>
                          <Badge variant={sv.variant} className="text-xs">
                            {sv.label}
                          </Badge>
                        </div>

                        <div className="grid gap-1.5 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="h-4 w-4" />
                            <span>{row.email}</span>
                          </div>
                          {row.location && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <MapPin className="h-4 w-4" />
                              <span>{row.location}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            <span>
                              Submitted{" "}
                              {formatDistanceToNow(new Date(row.created_at), {
                                addSuffix: true,
                              })}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() =>
                            navigate(`/admin/matchmaker-signups/${row.id}`)
                          }
                        >
                          Review
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
